import {
  AvatarUpload,
  Icon,
  RadioList,
  TextInput,
} from "@milestone/component-library";
import React, { useEffect, useState } from "react";
import { FieldArray, Formik } from "formik";
import AddIcon from "../../../../assets/Admin/add.png";
import TrashIcon from "../../../../assets/Admin/TrashCan";
import {
  ChildContainer,
  ChildWrapper,
  Container,
  Header,
  Section,
  TopSaveWrapper,
} from "../formComponents.styles";
import {
  BlueButton,
  FlatButton,
  OutlineButton,
  RemoveButton,
  SaveButtonContainer,
  StyledForm,
} from "../../Admin.styles";
import { Api } from "../../../../services/api";
import { useHistory, useParams } from "react-router-dom";
import { MemberDetails, Phone } from "../../../../services/api/types/Members";
import {
  makeUndefined,
  useMemberDetails,
} from "../../../../services/hooks/MemberDetails";
import { DateSelectWrapper, DropDownWrapper } from "../formComponents";
import {
  Child,
  GenderOptions,
  LivingOptions,
  MarriedOptions,
  Member,
  PersonalInformation,
  PersonalInfoSchema,
  Pet,
  PhoneOptions,
  SpeciesOptions,
  Spouse,
} from "./personalInfoSchema";
import { IndividualType } from "../../../../enums";
import { useDispatch } from "react-redux";
import { setUserProfilePicture } from "../../../../store/slices/AuthSlice";
import { useAppSelector } from "../../../../store/hooks";

const member: Member = {
  firstName: "Name",
  lastName: undefined,
  avatarImage: undefined,
  email: undefined,
  phone: undefined,
  gender: undefined,
  phoneDropdown: PhoneOptions[0],
  birthDate: undefined,
  marriedDate: undefined,
  deceasedDate: undefined,
  hireDate: undefined,
  maritalStatus: undefined,
  living: "Living",
};

let spouse: Spouse = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  phoneDropdown: PhoneOptions[0],
  avatarImage: "",
  gender: undefined,
  birthDate: undefined,
  deceasedDate: undefined,
  living: "Living",
};

const child: Child = {
  firstName: "",
  gender: "Male",
  birthDate: undefined,
  living: "Living",
  deceasedDate: undefined,
  lastName: undefined,
  email: undefined,
  phone: undefined,
};

const pet: Pet = {
  firstName: "",
  species: "Dog",
  birthDate: undefined,
  living: "Living",
  deceasedDate: undefined,
  lastName: undefined,
  email: undefined,
  phone: undefined,
};

const memberInfo: PersonalInformation = {
  member,
  spouse: [],
  children: [],
  pets: [],
  associateId: "",
};

export const PhonePrefix = "+1 ";

export const PersonalInfo: React.FC<{}> = () => {
  let oldMarriedValue = "Single";
  const history = useHistory();
  const [memberData, setMemberData] = useState<PersonalInformation>(memberInfo);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { memberId } = useParams<{ memberId: string }>();
  const memberDetails = useMemberDetails(memberId);
  const dispatch = useDispatch();
  const user = useAppSelector(state => state.auth.user);

  const updateMemberDetails = async () => {
    if (memberDetails) {
      const memberAvatar = await Api.members.getMemberProfilePicture(
        memberDetails.member.memberId
      );
      if (memberDetails.spouse) {
        memberDetails.spouse.avatarImage = await Api.members.getSpousePicture(
          memberDetails.member.memberId
        );
      }
      setMemberData(oldData => {
        const newData: PersonalInformation = {
          member: {
            ...oldData.member,
            avatarImage: memberAvatar,
            ...memberDetails.member,
          },
          spouse: memberDetails.spouse
            ? [
                {
                  living: "",
                  phoneDropdown: "",
                  ...oldData.spouse,
                  ...memberDetails.spouse,
                },
              ]
            : oldData.spouse,
          pets: memberDetails.pets || oldData.pets,
          children: memberDetails.children || oldData.children,
          associateId: memberDetails.associateId || oldData.associateId,
        };
        oldMarriedValue = newData.member.maritalStatus =
          newData.spouse && newData.spouse.length ? "Married" : "Single";
        // fix for phone number being undefined
        if (!newData.member.phone) newData.member.phone = "";
        if (memberDetails.member.phones) {
          const phone = memberDetails.member.phones[0];
          newData.member.phone = phone.phone;
          phone.phoneType = PhoneOptions.find(
            phoneType => phone.phoneType === phoneType.replaceAll(" ", "")
          ) as string;
          newData.member.phoneDropdown = phone.phoneType;
        }
        if (newData.spouse && newData.spouse.length) {
          newData.spouse[0].living = newData.spouse[0].deceasedDate
            ? LivingOptions[1]
            : LivingOptions[0];
        }
        if (newData.children) {
          for (const child of newData.children) {
            child.living = child.deceasedDate
              ? LivingOptions[1]
              : LivingOptions[0];
          }
        }
        if (newData.pets) {
          for (const child of newData.pets) {
            child.living = child.deceasedDate
              ? LivingOptions[1]
              : LivingOptions[0];
          }
        }
        return newData;
      });
      setDataLoaded(true);
    }
  };

  useEffect(() => {
    updateMemberDetails();
  }, [memberDetails]);

  const handleMarriedDropdown =
    (
      setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean
      ) => void,
      push: (object: any) => void,
      remove: <T>(index: number) => T | undefined
    ) =>
    (_field: string, value: typeof MarriedOptions[number]) => {
      if (value === "Married") {
        if (oldMarriedValue !== "Married") {
          push(spouse);
        }
      } else {
        const oldSpouse: any | undefined = remove(0);
        if (oldSpouse.id)
          Api.members.deleteIndividual(oldSpouse.memberId, oldSpouse.id);
        if (oldSpouse) spouse = oldSpouse;
      }
      oldMarriedValue = value;
      setFieldValue("member.maritalStatus", value);
    };

  const handleRemove = (
    person: any,
    remove: (i: number) => void,
    index: number
  ) => {
    // remove from DB
    if (person.id) Api.members.deleteIndividual(person.memberId, person.id);
    // remove from front end
    remove(index);
  };

  const submitValues = async (values: any) => {
    let data: MemberDetails = {
      member: { ...values.member },
      spouse: values.spouse.length ? { ...values.spouse[0] } : undefined,
      children: values.children,
      pets: values.pets,
      associateId: values.associateId,
    };
    //format member data
    if (data.member.phone) {
      data.member.phone = PhonePrefix + data.member.phone;
      if (values.member.phoneDropdown) {
        const phone: Phone = {
          phone: data.member.phone,
          phoneType: (values.member.phoneDropdown as string).replaceAll(
            " ",
            ""
          ),
        };
        data.member.phones = [phone];
      }
    }

    //format spouse data
    if (data.spouse) {
      if (data.spouse.phone) {
        console.log("spouse", data.spouse.phone);
        data.spouse.phone = PhonePrefix + data.spouse.phone;
        if (values.spouse.phoneDropdown) {
          const phone: Phone = {
            phone: data.spouse.phone,
            phoneType: (values.spouse.phoneDropdown as string).replaceAll(
              " ",
              ""
            ),
          };
          data.spouse.phones = [phone];
        }
      }
      data.spouse.type = IndividualType.Spouse;

      if (data.spouse.avatarImage) {
        if (typeof data.spouse.avatarImage !== "string") {
          try {
            await Api.members.updateSpousePicture(
              values.member.memberId,
              data.spouse.avatarImage
            );
          } catch (error) {
            console.error("Update Member Photo:", error);
          }
        }
        data.spouse.avatarImage = undefined;
      }
    }

    if (data.children) {
      data.children.map(child => (child.type = IndividualType.Child));
    }
    if (data.pets) {
      data.pets.map(child => (child.type = IndividualType.Pet));
    }

    data = makeUndefined(data);
    try {
      await Api.members.updateMemberDetails(memberId, data);
    } catch (error) {
      console.error("Update Team:", error);
    }
    if (values.member.avatarImage) {
      if (typeof values.member.avatarImage !== "string") {
        try {
          await Api.members.updateMemberProfilePicture(
            values.member.memberId,
            values.member.avatarImage
          );
          // if the user is the same as the member being edited update picture
          if (user.id === values.member.memberId) {
            dispatch(
              setUserProfilePicture(
                URL.createObjectURL(values.member.avatarImage)
              )
            );
          }
        } catch (error) {
          console.error("Update Member Photo:", error);
        }
      }
      data.member.avatarImage = undefined;
    }
    try {
      history.push(`/admin/members/${memberId}/teamMemberInformation`);
    } catch (error) {
      console.log("nav failed", error);
    }
  };

  const SaveButtons = (
    <SaveButtonContainer>
      <BlueButton type="submit">Save</BlueButton>
      <OutlineButton
        type="button"
        onClick={() => history.push("/admin/members")}
      >
        Cancel
      </OutlineButton>
    </SaveButtonContainer>
  );

  return (
    <Container>
      {dataLoaded && (
        <Formik
          initialValues={memberData}
          validationSchema={PersonalInfoSchema}
          onSubmit={submitValues}
        >
          {({ values, setFieldValue }) => (
            <StyledForm>
              <TopSaveWrapper>{SaveButtons}</TopSaveWrapper>
              <Header>Primary Information</Header>
              <Section>
                <AvatarUpload
                  setFieldValue={setFieldValue}
                  name="member.avatarImage"
                />
                <TextInput name="member.firstName" label="First Name" />
                <TextInput name="member.lastName" label="Last Name" />
                <TextInput name="member.email" label="Email" />
                <TextInput
                  name="member.phone"
                  label="Primary Phone"
                  mask={"(999) 999-9999"}
                  dropDown={PhoneOptions}
                  setFieldValue={setFieldValue}
                />
                <DateSelectWrapper
                  name="member.birthDate"
                  label="Birth Date"
                  monthDropdown={true}
                  yearDropdown={true}
                />
                <DateSelectWrapper
                  name="member.hireDate"
                  label="Hire Date"
                  monthDropdown={true}
                  yearDropdown={true}
                />
              </Section>
              <Header>Spouse</Header>
              <FieldArray name="spouse">
                {({ /* insert, */ remove, push }) => (
                  <Section>
                    <DropDownWrapper
                      label="Marital Status"
                      name="member.maritalStatus"
                      data={MarriedOptions}
                      setFieldValue={handleMarriedDropdown(
                        setFieldValue,
                        push,
                        remove
                      )}
                    />
                    {values.spouse &&
                      values.spouse.map((theSpouse, index) => {
                        return (
                          <Section key={index}>
                            <AvatarUpload
                              setFieldValue={setFieldValue}
                              name={`spouse[${index}].avatarImage`}
                            />
                            <TextInput
                              name={`spouse[${index}].firstName`}
                              label="Spouse's First Name"
                            />
                            <TextInput
                              name={`spouse[${index}].lastName`}
                              label="Last Name"
                            />
                            <TextInput
                              name={`spouse[${index}].email`}
                              label="Email"
                            />
                            <TextInput
                              name={`spouse[${index}].phone`}
                              label="Primary Phone"
                              mask={"(999) 999-9999"}
                              dropDown={PhoneOptions}
                              setFieldValue={setFieldValue}
                            />
                            <DateSelectWrapper
                              name={`spouse[${index}].birthDate`}
                              label="Birth Date"
                              monthDropdown={true}
                              yearDropdown={true}
                            />
                            <RadioList
                              name={`spouse[${index}].living`}
                              options={LivingOptions}
                            />
                            {theSpouse.living === "Deceased" && (
                              <DateSelectWrapper
                                name={`spouse[${index}].deceasedDate`}
                                label="Deceased Date"
                                monthDropdown={true}
                                yearDropdown={true}
                              />
                            )}
                          </Section>
                        );
                      })}
                  </Section>
                )}
              </FieldArray>
              <Header>Children</Header>
              <FieldArray name="children">
                {({ /* insert, */ remove, push }) => (
                  <Section>
                    <FlatButton type="button" onClick={() => push(child)}>
                      Add Child
                      <Icon src={AddIcon} alt="+" />
                    </FlatButton>
                    {values.children &&
                      values.children.map((child, index) => {
                        return (
                          <ChildWrapper key={index}>
                            <ChildContainer>
                              <TextInput
                                name={`children.[${index}].firstName`}
                                label="First Name"
                                dropDownName={`children.[${index}].gender`}
                                dropDown={GenderOptions}
                              />
                              <DateSelectWrapper
                                name={`children[${index}].birthDate`}
                                label="Birth Date"
                                monthDropdown={true}
                                yearDropdown={true}
                              />
                              <RadioList
                                name={`children.[${index}].living`}
                                options={LivingOptions}
                              />
                              {child.living === "Deceased" && (
                                <DateSelectWrapper
                                  name={`children[${index}].deceasedDate`}
                                  label="Deceased Date"
                                  monthDropdown={true}
                                  yearDropdown={true}
                                />
                              )}
                            </ChildContainer>
                            <RemoveButton
                              type="button"
                              onClick={() => handleRemove(child, remove, index)}
                            >
                              <TrashIcon />
                            </RemoveButton>
                          </ChildWrapper>
                        );
                      })}
                  </Section>
                )}
              </FieldArray>
              <Header>Pets</Header>
              <FieldArray name="pets">
                {({ /* insert,*/ remove, push }) => (
                  <Section>
                    <FlatButton type="button" onClick={() => push(pet)}>
                      Add Pet
                      <Icon src={AddIcon} alt="+" />
                    </FlatButton>
                    {values.pets &&
                      values.pets.map((child, index) => {
                        return (
                          <ChildWrapper key={index}>
                            <ChildContainer>
                              <TextInput
                                name={`pets.[${index}].firstName`}
                                label="First Name"
                                dropDownName={`pets.[${index}].species`}
                                dropDown={SpeciesOptions}
                              />
                              <DateSelectWrapper
                                name={`pets[${index}].birthDate`}
                                label="Birth Date"
                                monthDropdown={true}
                                yearDropdown={true}
                              />
                              <RadioList
                                name={`pets.[${index}].living`}
                                options={LivingOptions}
                              />
                              {child.living === "Deceased" && (
                                <DateSelectWrapper
                                  name={`pets[${index}].deceasedDate`}
                                  label="Deceased Date"
                                  monthDropdown={true}
                                  yearDropdown={true}
                                />
                              )}
                            </ChildContainer>
                            <RemoveButton
                              type="button"
                              onClick={() => handleRemove(child, remove, index)}
                            >
                              <TrashIcon />
                            </RemoveButton>
                          </ChildWrapper>
                        );
                      })}
                  </Section>
                )}
              </FieldArray>
              {SaveButtons}
              {/*errors && <div>{JSON.stringify(errors)}</div>*/}
            </StyledForm>
          )}
        </Formik>
      )}
    </Container>
  );
};
